import { useState } from "react";
import { ChevronDown } from "lucide-react";
import moment from "moment";

import editPencil from "assets/images/edit-green-pencil.svg";
import prescriptionIcon from "assets/images/pregnancy/prescription.svg";
import refferralIcon from "assets/images/pregnancy/refferral.svg";
import { SafeHtml } from "components/SafeHtml/SafeHtml";
import { stripHtml } from "utils/stripHtml";

import { ConsultationAttachment } from "./components/ConsultationAttachment/ConsultationAttachment";
import { ConsultationDateBox } from "./components/ConsultationDateBox/ConsultationDateBox";
import style from "./ConsultationPreview.module.css";

export const ConsultationPreview = ({
  consultation,
  children,
  text,
  previewText,
  notes = [],
  onEditConsultation,
  goToPatientConsult,
  isActive = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const previewHtml = notes.length > 0 ? notes[0]?.note || "" : text;
  const previewPlain =
    previewText || stripHtml(previewHtml) || "No notes found for this consultation.";

  const hasAttachments =
    (consultation?.attachments?.refferrals?.length ?? 0) > 0 ||
    (consultation?.attachments?.prescriptions?.length ?? 0) > 0;

  const hasExpandableContent =
    previewPlain.length > 80 ||
    notes.length > 1 ||
    !!children ||
    hasAttachments;

  return (
    <div className={`${style.consultationWrapper} ${isActive ? style.consultationWrapperActive : ""}`}>
      <ConsultationDateBox date={consultation.date} isActive={isActive} />
      <div className={style.consultationContentWrapper}>
        <div>
          <div className={style.consultationTitleWrapper}>
            <h2
              onClick={() =>
                goToPatientConsult?.(
                  consultation.patient?.id,
                  consultation.patient?.firstName
                )
              }
            >
              {consultation.title}
            </h2>
          </div>
          {consultation.consultationStaff?.firstName &&
            consultation.consultationStaff?.lastName && (
              <div className={style.consultationText}>
                {consultation.consultationStaff.firstName +
                  " " +
                  consultation.consultationStaff.lastName}
              </div>
            )}
          {!isExpanded && (
            <div className={style.consultationText}>{previewPlain}</div>
          )}
          {!isExpanded && (
            <div className={style.attachmentsInfo}>
              {consultation?.attachments?.refferrals?.length > 0 && (
                <p>
                  <img src={refferralIcon} alt="referral" />
                  Referral
                </p>
              )}
              {consultation?.attachments?.prescriptions?.length > 0 && (
                <p>
                  <img src={prescriptionIcon} alt="prescription" />
                  Prescription
                </p>
              )}
            </div>
          )}
        </div>
        {isExpanded && (
          <>
            {notes.length > 0 ? (
              <div className={style.notesExpandedList}>
                {notes.map((noteItem, index) => {
                  const date = noteItem.created
                    ? moment(noteItem.created).format("DD-MM-YYYY HH:mm")
                    : null;
                  const noteLabel =
                    notes.length > 1
                      ? `Note ${index + 1}${date ? ` — ${date}` : ""}`
                      : null;

                  return (
                    <div
                      key={noteItem.id ?? index}
                      className={style.noteExpandedItem}
                    >
                      {noteLabel && (
                        <div className={style.noteExpandedMeta}>
                          {noteLabel}
                        </div>
                      )}
                      <SafeHtml
                        html={noteItem.note}
                        className={`ql-editor ${style.noteContent}`}
                      />
                    </div>
                  );
                })}
              </div>
            ) : !children && text ? (
              <SafeHtml html={text} className={`ql-editor ${style.noteContent}`} />
            ) : null}
            {children}
            {(consultation?.attachments?.refferrals?.length > 0 ||
              consultation?.attachments?.prescriptions?.length > 0) && (
              <div>
                {consultation?.attachments?.prescriptions?.map(
                  (prescription) => (
                    <ConsultationAttachment
                      type="prescription"
                      attachment={prescription}
                      key={prescription.id}
                    />
                  )
                )}
                {consultation?.attachments?.refferrals?.map((refferral) => (
                  <ConsultationAttachment
                    type="refferral"
                    attachment={refferral}
                    key={refferral.id}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <div className={style.rowActions}>
        {!!onEditConsultation && (
          <button
            onClick={onEditConsultation}
            data-testid="consultation-view-button"
            className={style.viewButton}
          >
            View
            <img src={editPencil} alt="edit button" />
          </button>
        )}
        {hasExpandableContent ? (
          <button
            aria-label={isExpanded ? "collapse" : "expand"}
            onClick={() => setIsExpanded((prev) => !prev)}
            className={style.rowActionButton}
          >
            <ChevronDown
              size={18}
              style={{
                transform: isExpanded ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            />
          </button>
        ) : (
          <div className={style.rowActionPlaceholder} />
        )}
      </div>
    </div>
  );
};
